// src/pages/PathManagePatient/UpdateLabTestResultModal.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { COMPACT_FORM_MODAL_SX, STANDARD_FORM_HEADING_SX } from "../../utils/formModalStyles";
import { updateReportResult } from "../../services/pathLabTestReportService"; // Ensure this is the correct import
import { updatePatientPathologyResult } from "../../services/PatientPathologyService"; // Import the new service
import { assertPathBomStockForResultEntry } from "../../services/pathBomInventoryGate";
import PathBomInsufficientStockDialog from "../../components/PathBomInsufficientStockDialog";

// Update the validation schema to allow alphanumeric input
const validationSchema = Yup.object({
  result: Yup.string()
    .required("Result is required")
    .min(1, "Result cannot be empty"),
});

const UpdateLabTestResultModal = ({ open, onClose, reportId, test, refetchPathLabTestList }) => {
  const [loading, setLoading] = useState(false);
  const [initialResult, setInitialResult] = useState("");
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockChecks, setStockChecks] = useState([]);

  useEffect(() => {
    if (open) {
      // Fetch the current result if needed
      // For now, we will assume it's empty
      setInitialResult("");
    }
  }, [open]);

  const formik = useFormik({
    initialValues: { result: initialResult },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const stockGate = await assertPathBomStockForResultEntry({
          patientLabTests: [
            {
              ...test,
              labTestId: test?.labTestId || test?.testId || test?.id,
              testId: test?.labTestId || test?.testId || test?.id,
              testName: test?.testName,
            },
          ],
          alreadyCollected: true,
        });
        if (!stockGate.ok) {
          setStockChecks(stockGate.checks || []);
          setStockDialogOpen(true);
          return;
        }

        // Prepare the payload for the update
        const payload = {
          // result: values.result,
          result: values.result.toString(), // Convert to string
          resultStatus: "COMPLETE", // Ensure this is what the server expects
        };
        // Ensure reportId is defined
        if (!reportId) {
          throw new Error("Report ID is undefined");
        }
        // Call the updateReportResult function
        const response = await updateReportResult(reportId, payload);
        alert("Result updated successfully!");

        // Resolve prescriptionId robustly: prefer parent card id, fallback to matching in patientLabTests
        const prescriptionId = (test && test.id)
          ? test.id
          : (Array.isArray(test?.patientLabTests)
              ? test.patientLabTests.find(labTest => labTest.testName === test.testName)?.prescriptionId
              : undefined);

        // Now update the patient_pathology table
        if (!prescriptionId) {
          throw new Error("Missing prescriptionId for patient_pathology update");
        }
        await updatePatientPathologyResult(
          test.patientId,
          prescriptionId,
          response.testId,
          values.result,
          "COMPLETE",
          {
            reportId,
            invoiceId: test.invoiceId ?? test.invoice_id,
            patientType: "opd",
            labTestName: test.testName,
            patientName: test.patientName,
          }
        );

        // Inventory is deducted at sample collection — do not deduct again on result save.

        // Reset the form values
        formik.resetForm(); // Clear the result field
        onClose();
        refetchPathLabTestList(); // Call the refetch function passed as a prop
      } catch (error) {
        console.error("Error updating result:", error);
        alert("Failed to update result.");
      } finally {
        setLoading(false);
      }
    },
  });

  // Function to handle input change and allow alphanumeric input
  const handleResultChange = (event) => {
    const { value } = event.target;
    // Allow alphanumeric characters (letters, numbers, spaces, and common symbols)
    formik.setFieldValue("result", value);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose && onClose();
  };

  return (
    <>
    <PathBomInsufficientStockDialog
      open={stockDialogOpen}
      onClose={() => setStockDialogOpen(false)}
      checks={stockChecks}
      actionLabel="add / update result"
      title="Cannot save result — consumable not in inventory"
    />
    <BasicModal open={open} onClose={handleClose} sx={COMPACT_FORM_MODAL_SX}>
      <Box>
        <Typography sx={STANDARD_FORM_HEADING_SX}>
          Update Lab Test Result
        </Typography>
        {/* Details */}
        {test && (
          <Box sx={{ mb: 1, p: 1.25, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="body2"><strong>Patient Name:</strong> {test.patientName || '—'}</Typography>
            <Typography variant="body2"><strong>Doctor:</strong> {test.doctorName || '—'}</Typography>
            <Typography variant="body2"><strong>Prescription Date:</strong> {test.createdAt ? new Date(test.createdAt).toLocaleDateString(undefined, { day:'2-digit', month:'short', year:'numeric' }) : '—'}</Typography>
            <Typography variant="body2"><strong>Test:</strong> {(test.patientLabTests||[]).map(t=>t.testName).join(', ') || (test.testName || '—')}</Typography>
          </Box>
        )}
        {loading ? (
          <Box textAlign="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="Result"
              name="result"
              value={formik.values.result}
              onChange={handleResultChange} // Use the new handler for alphanumeric input
              error={formik.touched.result && Boolean(formik.errors.result)}
              helperText={formik.touched.result && formik.errors.result}
              sx={{ mb: 2 }}
            />
            <FormModalActions
              onCancel={handleClose}
              submitType="submit"
              loading={formik.isSubmitting}
            />
          </form>
        )}
      </Box>
    </BasicModal>
    </>
  );
};

export default UpdateLabTestResultModal;
